/**
 * Dual-mode console log collector.
 *
 * Mode 1 (BiDi): Subscribes to `log.entryAdded` events for real-time capture.
 * Mode 2 (Polling): Falls back to `getLogs('browser')` or `getLogs('safariConsole')`.
 */

import type { DeviceLogEntry, DeviceLogSource, Platform } from './types.js';
import { JsonlFileWriter } from './jsonl-stream.js';

export interface ConsoleLogCollectorOptions {
  readonly enabled: boolean;
  readonly preferBiDi: boolean;
  readonly platform: Platform;
  readonly pollInterval: number;
}

type LogEntry = { level: string; timestamp: number; message: string };
type DriverLike = {
  getLogs(type: string): Promise<LogEntry[]>;
  getLogTypes(): Promise<string[]>;
  sessionSubscribe?(params: { events: string[] }): Promise<void>;
  on?(event: string, handler: (...args: unknown[]) => void): void;
};

function normalizeLevel(raw: string): DeviceLogEntry['level'] {
  const lower = raw.toLowerCase();
  if (lower === 'verbose') return 'verbose';
  if (lower === 'debug') return 'debug';
  if (lower.includes('warn')) return 'warn';
  if (lower.includes('err') || lower === 'severe') return 'error';
  return 'info';
}

export class ConsoleLogCollector {
  private readonly writer: JsonlFileWriter;
  private readonly options: ConsoleLogCollectorOptions;
  private intervalHandle: ReturnType<typeof setInterval> | null = null;
  private driver: DriverLike | null = null;
  private useBiDi = false;
  private pollingLogType: string | null = null;
  private disposed = false;

  constructor(options: ConsoleLogCollectorOptions) {
    this.options = options;
    this.writer = new JsonlFileWriter('console-logs');
  }

  async start(driver: DriverLike): Promise<void> {
    if (!this.options.enabled || this.disposed) return;
    this.driver = driver;

    if (this.options.preferBiDi && driver.sessionSubscribe && driver.on) {
      try {
        await driver.sessionSubscribe({ events: ['log.entryAdded'] });
        driver.on('log.entryAdded', this.handleBiDiLog);
        this.useBiDi = true;
        return;
      } catch {
        // BiDi not available; fall back to polling
      }
    }

    let available: string[] = [];
    try {
      available = await driver.getLogTypes();
    } catch {
      available = [];
    }

    if (this.options.platform === 'ios' && available.includes('safariConsole')) {
      this.pollingLogType = 'safariConsole';
    } else if (available.includes('browser')) {
      this.pollingLogType = 'browser';
    }

    if (this.pollingLogType) {
      this.intervalHandle = setInterval(() => {
        void this.poll();
      }, this.options.pollInterval);
    }
  }

  private handleBiDiLog = (...args: unknown[]): void => {
    if (this.disposed) return;
    const event = args[0] as Record<string, unknown> | undefined;
    if (!event) return;

    const entry: DeviceLogEntry = {
      timestamp: new Date().toISOString(),
      level: normalizeLevel(String(event.level ?? 'info')),
      message: String(event.text ?? event.message ?? ''),
      source: 'bidi',
    };
    this.writer.append(entry);
  };

  private async poll(): Promise<void> {
    if (!this.driver || this.disposed || !this.pollingLogType) return;
    try {
      const entries = await this.driver.getLogs(this.pollingLogType);
      for (const entry of entries) {
        const normalized: DeviceLogEntry = {
          timestamp: new Date(entry.timestamp).toISOString(),
          level: normalizeLevel(entry.level),
          message: entry.message,
          source: this.pollingLogType as DeviceLogSource,
        };
        this.writer.append(normalized);
      }
    } catch {
      // Silently skip
    }
  }

  isBiDiActive(): boolean {
    return this.useBiDi;
  }

  getPath(): string {
    return this.writer.getPath();
  }

  getCount(): number {
    return this.writer.getCount();
  }

  stop(): void {
    if (this.intervalHandle !== null) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.writer.close();
  }

  cleanup(): void {
    this.dispose();
    this.writer.cleanup();
  }
}
